import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Users, ShoppingCart, DollarSign, Mail, Package, 
  Clock, CheckCircle, Search, Filter, ExternalLink,
  ChevronDown, ChevronRight, Eye, Send, X, RefreshCw,
  Shield, Smartphone, Key
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { API, useAuth } from "@/App";

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deliveryLink, setDeliveryLink] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [adminNotes, setAdminNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  
  // 2FA State
  const [twoFAStatus, setTwoFAStatus] = useState(null); // null = checking, 'verified' = ok, 'needs_code' = ask code, 'needs_setup' = setup
  const [twoFACode, setTwoFACode] = useState("");
  const [twoFASecret, setTwoFASecret] = useState(null);
  const [twoFALoading, setTwoFALoading] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  // Check 2FA status on load
  useEffect(() => {
    if (user) {
      check2FAStatus();
    }
  }, [user]);

  const check2FAStatus = async () => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/auth/2fa/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (!data.required) {
        // Not an admin, redirect
        navigate("/dashboard");
        return;
      }
      
      if (data.enabled) {
        // Check if we have a valid admin session
        const adminToken = sessionStorage.getItem("admin_2fa_verified");
        if (adminToken) {
          setTwoFAStatus("verified");
          fetchData();
        } else {
          setTwoFAStatus("needs_code");
        }
      } else {
        setTwoFAStatus("needs_setup");
      }
    } catch (error) {
      console.error("2FA check error:", error);
      setTwoFAStatus("needs_code");
    }
  };

  const setup2FA = async () => {
    setTwoFALoading(true);
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/auth/2fa/setup`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setTwoFASecret(data);
      } else {
        toast.error(data.detail || "Failed to setup 2FA");
      }
    } catch (error) {
      toast.error("Error setting up 2FA");
    }
    setTwoFALoading(false);
  };

  const verify2FA = async (isSetup = false) => {
    if (twoFACode.length !== 6) {
      toast.error("Enter a 6-digit code");
      return;
    }
    
    setTwoFALoading(true);
    const token = localStorage.getItem("token");
    const endpoint = isSetup ? "/auth/2fa/verify" : "/admin/verify-2fa";
    
    try {
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ code: twoFACode })
      });
      const data = await res.json();
      
      if (res.ok && data.verified) {
        if (isSetup) {
          toast.success("2FA enabled successfully!");
          setTwoFASecret(null);
        }
        sessionStorage.setItem("admin_2fa_verified", "true");
        setTwoFAStatus("verified");
        setTwoFACode("");
        fetchData();
      } else {
        toast.error(data.detail || "Invalid code");
      }
    } catch (error) {
      toast.error("Verification failed");
    }
    setTwoFALoading(false);
  };

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [statsRes, usersRes, ordersRes, contactsRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { headers }),
        fetch(`${API}/admin/users`, { headers }),
        fetch(`${API}/admin/orders`, { headers }),
        fetch(`${API}/admin/contacts`, { headers })
      ]);

      if (statsRes.status === 403) {
        toast.error("Access denied. Admin privileges required.");
        navigate("/dashboard");
        return;
      }

      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (ordersRes.ok) setOrders(await ordersRes.json());
      if (contactsRes.ok) setContacts(await contactsRes.json());
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load admin data");
    }
    setLoading(false);
  };

  const handleDeliver = async (orderId) => {
    if (!deliveryLink.trim()) {
      toast.error("Please enter a delivery link");
      return;
    }

    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/admin/orders/${orderId}/deliver`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ delivery_link: deliveryLink })
      });

      if (res.ok) {
        toast.success("Order delivered! Customer has been notified.");
        setSelectedOrder(null);
        setDeliveryLink("");
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to deliver order");
      }
    } catch (error) {
      toast.error("Error delivering order");
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    setSavingStatus(true);
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/admin/orders/${orderId}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success("Status updated!");
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to update status");
      }
    } catch (error) {
      toast.error("Error updating status");
    }
    setSavingStatus(false);
  };

  const handleSaveNotes = async (orderId) => {
    setSavingNotes(true);
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/admin/orders/${orderId}/notes`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ admin_notes: adminNotes })
      });

      if (res.ok) {
        toast.success("Notes saved!");
        setSelectedOrder(prev => ({ ...prev, admin_notes: adminNotes }));
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to save notes");
      }
    } catch (error) {
      toast.error("Error saving notes");
    }
    setSavingNotes(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.order_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-slate-300 border-t-[#3C59B2] rounded-full animate-spin"></div>
      </div>
    );
  }

  // 2FA Setup Screen
  if (twoFAStatus === "needs_setup") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-[#3C59B2]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-[#3C59B2]" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Sécurité Admin</h1>
            <p className="text-slate-600 mt-2">
              L'authentification à deux facteurs (2FA) est requise pour accéder à l'admin.
            </p>
          </div>

          {!twoFASecret ? (
            <Button 
              onClick={setup2FA} 
              disabled={twoFALoading}
              className="w-full bg-[#3C59B2] hover:bg-[#2d4a8f] text-white"
            >
              {twoFALoading ? "Chargement..." : "Configurer Google Authenticator"}
            </Button>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-slate-600 mb-3">
                  Scannez ce QR code avec Google Authenticator :
                </p>
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&ecc=M&data=${encodeURIComponent(twoFASecret.qr_data)}`}
                  alt="QR Code"
                  className="mx-auto border-2 rounded-lg p-2 bg-white"
                  style={{ width: 250, height: 250 }}
                />
                <p className="text-xs text-slate-500 mt-3">
                  Ou entrez ce code manuellement :<br/>
                  <code className="bg-slate-100 px-2 py-1 rounded text-sm font-mono">
                    {twoFASecret.secret}
                  </code>
                </p>
              </div>
              
              <div>
                <label className="text-sm text-slate-600 block mb-1">
                  Entrez le code à 6 chiffres :
                </label>
                <Input
                  type="text"
                  maxLength={6}
                  placeholder="000000"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl tracking-widest"
                />
              </div>
              
              <Button 
                onClick={() => verify2FA(true)}
                disabled={twoFALoading || twoFACode.length !== 6}
                className="w-full bg-[#3C59B2] hover:bg-[#2d4a8f] text-white"
              >
                {twoFALoading ? "Vérification..." : "Activer 2FA"}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // 2FA Code Entry Screen
  if (twoFAStatus === "needs_code") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-[#3C59B2]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Smartphone className="w-8 h-8 text-[#3C59B2]" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Vérification 2FA</h1>
            <p className="text-slate-600 mt-2">
              Entrez le code de Google Authenticator
            </p>
          </div>

          <div className="space-y-4">
            <Input
              type="text"
              maxLength={6}
              placeholder="000000"
              value={twoFACode}
              onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
              className="text-center text-2xl tracking-widest"
              autoFocus
            />
            
            <Button 
              onClick={() => verify2FA(false)}
              disabled={twoFALoading || twoFACode.length !== 6}
              className="w-full bg-[#3C59B2] hover:bg-[#2d4a8f] text-white"
            >
              {twoFALoading ? "Vérification..." : "Accéder à l'Admin"}
            </Button>
            
            <p className="text-xs text-center text-slate-500">
              Session valide 30 minutes
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Still checking 2FA or loading data
  if (twoFAStatus !== "verified" || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-slate-300 border-t-[#3C59B2] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold text-slate-900">Cliptour Admin</h1>
              <span className="text-sm text-slate-500">Welcome, {user?.name}</span>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate("/")}>
                View Site
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-slate-200 pb-4">
          {[
            { id: "overview", label: "Overview", icon: Package },
            { id: "orders", label: "Orders", icon: ShoppingCart },
            { id: "users", label: "Users", icon: Users },
            { id: "contacts", label: "Contacts", icon: Mail }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? "bg-[#3C59B2] text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && stats && (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Revenue"
                value={`$${stats.total_revenue?.toLocaleString() || 0}`}
                icon={DollarSign}
                color="bg-green-500"
              />
              <StatCard
                title="Total Orders"
                value={stats.total_orders}
                subtitle={`${stats.delivered_orders} delivered`}
                icon={ShoppingCart}
                color="bg-[#3C59B2]"
              />
              <StatCard
                title="Total Users"
                value={stats.total_users}
                subtitle={`+${stats.users_this_month} this month`}
                icon={Users}
                color="bg-purple-500"
              />
              <StatCard
                title="Pending Delivery"
                value={stats.paid_orders}
                icon={Clock}
                color="bg-orange-500"
              />
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-4">Orders This Month</h3>
                <p className="text-3xl font-bold text-[#3C59B2]">{stats.orders_this_month}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-4">Contact Requests</h3>
                <p className="text-3xl font-bold text-[#3C59B2]">{stats.total_contacts}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-4">Delivery Rate</h3>
                <p className="text-3xl font-bold text-green-600">
                  {stats.total_orders > 0 
                    ? Math.round((stats.delivered_orders / stats.total_orders) * 100)
                    : 0}%
                </p>
              </div>
            </div>

            {/* Recent Orders */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <div className="p-6 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900">Recent Orders</h3>
              </div>
              <div className="divide-y divide-slate-100">
                {orders.slice(0, 5).map(order => (
                  <div key={order.order_id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                    <div>
                      <p className="font-medium text-slate-900">{order.order_id}</p>
                      <p className="text-sm text-slate-500">{order.user_email}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">${order.amount}</p>
                      <StatusBadge status={order.status} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === "orders" && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-slate-200 rounded-lg bg-white"
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="confirmed">Confirmed</option>
                <option value="in_progress">In Progress</option>
                <option value="on_hold">On Hold</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {/* Orders Table */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Order ID</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Service</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredOrders.map(order => (
                      <tr key={order.order_id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{order.order_id}</td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-slate-900">{order.user_email}</p>
                          <p className="text-xs text-slate-500">{order.company_name || "N/A"}</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {order.service_type === "cliptour_staged" ? "Cliptour Staged" : "Cliptour"}
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-slate-900">${order.amount}</td>
                        <td className="px-6 py-4"><StatusBadge status={order.status} /></td>
                        <td className="px-6 py-4 text-sm text-slate-500">{formatDate(order.created_at)}</td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <Button size="sm" variant="ghost" onClick={() => { setSelectedOrder(order); setAdminNotes(order.admin_notes || ""); }}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            {(order.status === "paid" || order.status === "confirmed" || order.status === "in_progress") && (
                              <Button size="sm" variant="outline" onClick={() => { setSelectedOrder(order); setAdminNotes(order.admin_notes || ""); }}>
                                <Send className="w-4 h-4 mr-1" />
                                Deliver
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="space-y-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 max-w-md"
              />
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Company</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Orders</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Joined</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredUsers.map(u => (
                      <tr key={u.user_id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{u.name}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{u.email}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{u.company_name || "N/A"}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-slate-900">{u.order_count}</td>
                        <td className="px-6 py-4 text-sm text-slate-500">{formatDate(u.created_at)}</td>
                        <td className="px-6 py-4">
                          <Button size="sm" variant="ghost" onClick={() => setSelectedUser(u)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === "contacts" && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Company</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Properties</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Message</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {contacts.map(contact => (
                      <tr key={contact.contact_id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{contact.name}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          <a href={`mailto:${contact.email}`} className="text-[#3C59B2] hover:underline">
                            {contact.email}
                          </a>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">{contact.company_name || "N/A"}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{contact.num_properties || "N/A"}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">{contact.message}</td>
                        <td className="px-6 py-4 text-sm text-slate-500">{formatDate(contact.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Order Details</h2>
              <button onClick={() => { setSelectedOrder(null); setDeliveryLink(""); setAdminNotes(""); }} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Order ID</p>
                  <p className="font-semibold text-slate-900">{selectedOrder.order_id}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Status</p>
                  <select
                    value={selectedOrder.status}
                    onChange={(e) => handleStatusChange(selectedOrder.order_id, e.target.value)}
                    disabled={savingStatus}
                    className="px-3 py-1.5 border border-slate-200 rounded-lg bg-white text-sm font-medium"
                  >
                    <option value="pending">Pending Payment</option>
                    <option value="paid">Paid</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="in_progress">In Progress</option>
                    <option value="on_hold">On Hold</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Customer Email</p>
                  <p className="font-medium text-slate-900">{selectedOrder.user_email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Company</p>
                  <p className="font-medium text-slate-900">{selectedOrder.company_name || "N/A"}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Service</p>
                  <p className="font-medium text-slate-900">
                    {selectedOrder.service_type === "cliptour_staged" ? "Cliptour Staged" : "Cliptour"}
                    {selectedOrder.is_b2b ? " (B2B)" : ""}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Amount</p>
                  <p className="font-bold text-slate-900">${selectedOrder.amount}</p>
                  {selectedOrder.discount_applied ? <span className="text-xs text-green-600">First order discount</span> : null}
                </div>
              </div>

              <div>
                <p className="text-sm text-slate-500 mb-1">Photo Link</p>
                <a href={selectedOrder.photo_link} target="_blank" rel="noopener noreferrer" className="text-[#3C59B2] hover:underline flex items-center gap-1">
                  {selectedOrder.photo_link}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>

              {selectedOrder.notes && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Notes</p>
                  <p className="text-slate-700 bg-slate-50 p-3 rounded-lg">{selectedOrder.notes}</p>
                </div>
              )}

              {selectedOrder.service_suggestion && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Service Suggestions</p>
                  <p className="text-slate-700 bg-slate-50 p-3 rounded-lg">{selectedOrder.service_suggestion}</p>
                </div>
              )}

              {/* Admin Notes Section */}
              <div className="border-t border-slate-200 pt-4">
                <p className="text-sm text-slate-500 mb-2">Admin Notes (internal)</p>
                <Textarea
                  placeholder="Add internal notes about this order..."
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="mb-2"
                  rows={3}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleSaveNotes(selectedOrder.order_id)}
                  disabled={savingNotes}
                >
                  {savingNotes ? "Saving..." : "Save Notes"}
                </Button>
              </div>

              {selectedOrder.delivery_link && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Delivery Link</p>
                  <a href={selectedOrder.delivery_link} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline flex items-center gap-1">
                    {selectedOrder.delivery_link}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              )}

              {(selectedOrder.status === "paid" || selectedOrder.status === "confirmed" || selectedOrder.status === "in_progress") && (
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Deliver Order</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-slate-600 mb-1 block">Delivery Link (Dropbox/Drive)</label>
                      <Input
                        placeholder="https://dropbox.com/... or https://drive.google.com/..."
                        value={deliveryLink}
                        onChange={(e) => setDeliveryLink(e.target.value)}
                      />
                    </div>
                    <Button 
                      onClick={() => handleDeliver(selectedOrder.order_id)}
                      className="w-full bg-[#3C59B2] hover:bg-[#2d4a8f] text-white"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send Delivery & Notify Customer
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">User Details</h2>
              <button onClick={() => setSelectedUser(null)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-slate-500">Name</p>
                <p className="font-semibold text-slate-900">{selectedUser.name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <a href={`mailto:${selectedUser.email}`} className="text-[#3C59B2] hover:underline">
                  {selectedUser.email}
                </a>
              </div>
              <div>
                <p className="text-sm text-slate-500">Company</p>
                <p className="font-medium text-slate-900">{selectedUser.company_name || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">User ID</p>
                <p className="font-mono text-sm text-slate-600">{selectedUser.user_id}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Orders</p>
                <p className="font-bold text-slate-900">{selectedUser.order_count}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Joined</p>
                <p className="text-slate-600">{formatDate(selectedUser.created_at)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({ title, value, subtitle, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500 mb-1">{title}</p>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
}

// Status Badge Component
function StatusBadge({ status }) {
  const styles = {
    pending: "bg-slate-100 text-slate-600",
    paid: "bg-orange-100 text-orange-700",
    confirmed: "bg-orange-100 text-orange-700",
    in_progress: "bg-blue-100 text-blue-700",
    on_hold: "bg-yellow-100 text-yellow-700",
    delivered: "bg-green-100 text-green-700",
    cancelled: "bg-red-100 text-red-700"
  };

  const labels = {
    pending: "Pending Payment",
    paid: "Paid",
    confirmed: "Confirmed",
    in_progress: "In Progress",
    on_hold: "On Hold",
    delivered: "Delivered",
    cancelled: "Cancelled"
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
      {labels[status] || status}
    </span>
  );
}
